import React, { useState } from "react";

import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";

import AccountBalanceIcon from "@material-ui/icons/AccountBalance";

import {
  createWcPayout,
  clearErrorCreate,
} from "../../../redux/features/sales/payoutsSlice";

const useStyles = makeStyles((theme) => ({
  dialogTitleRoot: {
    paddingBottom: theme.spacing(1.5),
  },
  dialogTitleInner: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  titleLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  transferBadge: {
    textAlign: "right",
  },
  metaRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  metaLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 2,
  },
  metaValue: {
    fontWeight: 600,
    fontSize: "1rem",
  },
  divider: {
    margin: theme.spacing(0, 0, 2),
  },
  amountRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: theme.spacing(1),
  },
  amountLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  amountValue: {
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  footnote: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(-0.5),
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    paddingTop: theme.spacing(1.5),
    borderTop: `2px solid ${theme.palette.divider}`,
  },
  tableContainer: {
    maxHeight: 260,
    overflowY: "auto",
    marginBottom: theme.spacing(2),
  },
  tableCell: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.75, 1),
  },
  commentsField: {
    marginTop: theme.spacing(1),
  },
  errorAlert: {
    marginTop: theme.spacing(1),
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(0,0,0,0.65)"
        : "rgba(255,255,255,0.75)",
    zIndex: 10,
    borderRadius: theme.shape.borderRadius,
  },
}));

const fmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const WcPayoutPreviewDialog = ({ open, onClose, summary, onSuccess }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isCreating, errorCreate } = useSelector((state) => state.payouts);

  const [comments, setComments] = useState("");

  const handleClose = () => {
    if (isCreating) return;
    dispatch(clearErrorCreate());
    onClose();
  };

  const handleConfirm = async () => {
    dispatch(clearErrorCreate());
    const result = await dispatch(
      createWcPayout({
        account_id: summary.property.account_id,
        order_ids: summary.orders.map((o) => o.wc_order_id),
        comments: comments.trim() || undefined,
      })
    );
    if (!result.error) {
      setComments("");
      onSuccess(result.data);
    }
  };

  if (!summary) return null;

  const cell = classes.tableCell;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { position: "relative" } }}
    >
      <DialogTitle
        disableTypography
        classes={{ root: classes.dialogTitleRoot }}
      >
        <div className={classes.dialogTitleInner}>
          <div className={classes.titleLeft}>
            <AccountBalanceIcon color="primary" />
            <Typography variant="h6">WooCommerce Payout Preview</Typography>
          </div>
          <div className={classes.transferBadge}>
            <Typography variant="caption" color="textSecondary" display="block">
              Transfer Amount
            </Typography>
            <Typography
              variant="h5"
              style={{ color: "#19a86d", fontWeight: 700, lineHeight: 1.2 }}
            >
              {fmt.format(summary.totalTransfer)}
            </Typography>
          </div>
        </div>
      </DialogTitle>

      {isCreating && (
        <div className={classes.loadingOverlay}>
          <CircularProgress size={48} />
          <Typography variant="body1">
            Verifying charges with Stripe and sending payout…
          </Typography>
        </div>
      )}

      <DialogContent dividers>
        <Grid container spacing={3} className={classes.metaRow}>
          <Grid item xs={12} sm={4}>
            <Typography className={classes.metaLabel}>Property</Typography>
            <Typography className={classes.metaValue}>
              {summary.property.staying_at}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography className={classes.metaLabel}>Bank Account</Typography>
            <Typography className={classes.metaValue}>
              {summary.property.stripe_account_name}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography className={classes.metaLabel}>
              Commission Rate
            </Typography>
            <Typography className={classes.metaValue}>
              {summary.commissionPct}%
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography className={classes.metaLabel}>
              Orders Included
            </Typography>
            <Typography className={classes.metaValue}>
              {summary.orders.length}
            </Typography>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        <div className={classes.amountRow}>
          <Typography className={classes.amountLabel}>
            Total Orders Amount
          </Typography>
          <Typography className={classes.amountValue}>
            {fmt.format(summary.totalOrdersAmount)}
          </Typography>
        </div>
        <div className={classes.amountRow}>
          <Typography className={classes.amountLabel}>
            Stripe Processing Fees
          </Typography>
          <Typography className={classes.amountValue}>
            −{fmt.format(summary.totalStripeFees)}
          </Typography>
        </div>
        <div className={classes.amountRow}>
          <Typography className={classes.amountLabel}>
            Our Commission ({summary.commissionPct}%)*
          </Typography>
          <Typography className={classes.amountValue}>
            −{fmt.format(summary.totalCommission)}
          </Typography>
        </div>
        <Typography className={classes.footnote}>
          * Applies to the order total after the Stripe processing fee.
        </Typography>
        <div className={classes.totalRow}>
          <Typography variant="body1" style={{ fontWeight: 700 }}>
            Transfer Amount
          </Typography>
          <Typography
            variant="h6"
            style={{ color: "#19a86d", fontWeight: 700 }}
          >
            {fmt.format(summary.totalTransfer)}
          </Typography>
        </div>

        <Typography
          variant="subtitle2"
          style={{ marginTop: 20, marginBottom: 4 }}
        >
          Order Breakdown
        </Typography>
        <div className={classes.tableContainer}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={cell}>Order ID</TableCell>
                <TableCell className={cell}>Customer</TableCell>
                <TableCell className={cell}>Reservation</TableCell>
                <TableCell className={cell}>Date Placed</TableCell>
                <TableCell className={cell}>Check In</TableCell>
                <TableCell className={cell} align="right">
                  Order Total
                </TableCell>
                <TableCell className={cell} align="right">
                  Stripe Fee
                </TableCell>
                <TableCell className={cell} align="right">
                  Commission
                </TableCell>
                <TableCell className={cell} align="right">
                  Transfer
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.orders.map((o) => (
                <TableRow key={o.wc_order_id}>
                  <TableCell className={cell}>{o.wc_order_id}</TableCell>
                  <TableCell className={cell}>{o.customer_name}</TableCell>
                  <TableCell className={cell}>
                    {[o.reservation_name, o.reservation_number]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </TableCell>
                  <TableCell className={cell} style={{ whiteSpace: "nowrap" }}>
                    {o.date_order_placed
                      ? moment(o.date_order_placed)
                          .tz("America/Chicago")
                          .format("ll")
                      : "—"}
                  </TableCell>
                  <TableCell className={cell} style={{ whiteSpace: "nowrap" }}>
                    {o.check_in
                      ? moment(o.check_in, "YYYY-MM-DD").format("ll")
                      : "—"}
                  </TableCell>
                  <TableCell className={cell} align="right">
                    {fmt.format(o.total)}
                  </TableCell>
                  <TableCell className={cell} align="right">
                    {fmt.format(o.stripe_fee)}
                  </TableCell>
                  <TableCell className={cell} align="right">
                    {fmt.format(o.commission)}
                  </TableCell>
                  <TableCell
                    className={cell}
                    align="right"
                    style={{ color: "#19a86d", fontWeight: "bold" }}
                  >
                    {fmt.format(o.transfer_amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <TextField
          className={classes.commentsField}
          label="Comments (optional)"
          multiline
          rows={2}
          variant="outlined"
          fullWidth
          size="small"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          disabled={isCreating}
          inputProps={{ maxLength: 500 }}
          placeholder="e.g. October 2026 upgrade payout"
        />

        {errorCreate && (
          <Alert severity="error" className={classes.errorAlert}>
            {errorCreate.errorMessage ||
              "An error occurred while processing the payout. Please try again."}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={isCreating}
          startIcon={
            isCreating ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {isCreating ? "Processing…" : "Confirm & Send Payout"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WcPayoutPreviewDialog;
